import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CountryToken, EnumToken } from '@sleed/bizcommerce-api';
import { countriesApi } from '~/lib/api/client';
import { formToToken, type CountryFormValues } from '~/lib/schemas/country';

export const countryKeys = {
	all: ['countries'] as const,
	list: () => [...countryKeys.all, 'list'] as const,
	classifications: () => [...countryKeys.all, 'classifications'] as const
};

/** Full country list (legacy `/countries/getall`, loadMode 'raw' — all rows, no server paging). */
export function useCountriesList() {
	return useQuery<CountryToken[]>({
		queryKey: countryKeys.list(),
		queryFn: async () => (await countriesApi.getAll()).data.items ?? []
	});
}

/** Classification lookup (EnumToken {id, nm}) for the `cls` column + edit dropdown. */
export function useCountryClassifications() {
	return useQuery<EnumToken[]>({
		queryKey: countryKeys.classifications(),
		queryFn: async () => (await countriesApi.getClassifications()).data.items ?? [],
		staleTime: 5 * 60 * 1000
	});
}

/** Create / update / delete a country (parity with the legacy CustomStore insert/update/remove). */
export function useCountryMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: countryKeys.list() });

	const create = useMutation({
		mutationFn: async (form: CountryFormValues) => (await countriesApi.insert(formToToken(form))).data,
		onSuccess: invalidate
	});

	const update = useMutation({
		mutationFn: async (form: CountryFormValues) => (await countriesApi.update(formToToken(form))).data,
		onSuccess: invalidate
	});

	const remove = useMutation({
		mutationFn: async (id: number) => (await countriesApi.remove(id)).data,
		onSuccess: invalidate
	});

	return { create, update, remove };
}
